import { MiniMap } from '../maps';

// Place data from backend
export interface PlaceData {
  placeId: string;
  name: string;
  address: string;
  location: { lat: number; lng: number };
  rating?: number;
  photoUrl?: string;
  pinStyle: string;
  hours?: {
    weekdayText?: string[];
    openNow?: boolean;
  };
  reviews?: Array<{
    authorName: string;
    rating: number;
    text: string;
    time: string;
  }>;
  phone?: string;
  website?: string;
  userRatingsTotal?: number;
}

export interface GuidebookPage {
  id: string;
  title: string;
  html: string;
  pageType?: 'custom' | 'place';
  placeData?: PlaceData;
  importedAt?: string | null;
  sourceId?: string | null;
}

interface GuidebookPageBodyProps {
  page: GuidebookPage;
  googleMapsApiKey?: string;
  /** Places pages render an interactive map; the print view (BES-8422) turns it off. */
  showMap?: boolean;
  onMapClick?: () => void;
}

/**
 * The body of one guidebook page: the places card + details for `place` pages, and the
 * host-authored HTML (class `guidebook-content`, styled in styles/guidebook.css) for
 * everything. Shared by the on-screen page view (Guidebook.tsx) and the printable
 * all-pages view (GuidebookPrint.tsx) so both render identical markup.
 */
export default function GuidebookPageBody({
  page,
  googleMapsApiKey = '',
  showMap = true,
  onMapClick,
}: GuidebookPageBodyProps) {
  const isPlacePage = page.pageType === 'place' && !!page.placeData;

  return (
    <>
      {isPlacePage ? (
        // Place Page with Map and Details
        <div className="space-y-6">
          {/* Mini Map */}
          {showMap && googleMapsApiKey && (
            <MiniMap
              place={{
                lat: page.placeData!.location.lat,
                lng: page.placeData!.location.lng,
                name: page.placeData!.name,
              }}
              pinStyle={page.placeData!.pinStyle}
              apiKey={googleMapsApiKey}
              height="300px"
              onClick={onMapClick}
            />
          )}

          {/* Place Info Card - Photo + Quick Info */}
          <div className="print-card bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              {/* Photo - Only show if available */}
              {page.placeData!.photoUrl && (
                <div className="w-full sm:w-48 h-48 sm:h-auto flex-shrink-0">
                  <img
                    src={page.placeData!.photoUrl}
                    alt={page.placeData!.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Info Section */}
              <div className="flex-1 p-4">
                {/* Rating */}
                {page.placeData!.rating && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <span className="text-yellow-500 text-lg">★</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {page.placeData!.rating.toFixed(1)}
                    </span>
                    {page.placeData!.userRatingsTotal && (
                      <span className="text-sm text-gray-500">
                        ({page.placeData!.userRatingsTotal} reviews)
                      </span>
                    )}
                  </div>
                )}

                {/* Contact Info - Stack vertically for better mobile */}
                <div className="space-y-2 text-sm">
                  {page.placeData!.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-16">Phone:</span>
                      <a
                        href={`tel:${page.placeData!.phone}`}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {page.placeData!.phone}
                      </a>
                    </div>
                  )}

                  {page.placeData!.website && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-16">Website:</span>
                      <a
                        href={page.placeData!.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-medium truncate"
                      >
                        Visit website →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Place Details Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hours Card */}
            {page.placeData!.hours?.weekdayText && (
              <div className="print-card bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Hours</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {page.placeData!.hours.weekdayText.map((day, idx) => (
                    <div key={idx}>{day}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Address Card */}
            <div className="print-card bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Address</h3>
              <div className="text-sm text-gray-600 mb-3">{page.placeData!.address}</div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${page.placeData!.location.lat},${page.placeData!.location.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Open in Google Maps →
              </a>
            </div>
          </div>

          {/* Custom HTML Content (if any) */}
          {page.html && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Additional Information</h3>
              <div
                dangerouslySetInnerHTML={{ __html: page.html }}
                className="guidebook-content max-w-none"
                style={{
                  lineHeight: 1.7,
                  color: '#1f2937',
                }}
              />
            </div>
          )}
        </div>
      ) : (
        // Custom Page - just HTML
        <div
          dangerouslySetInnerHTML={{ __html: page.html }}
          className="guidebook-content max-w-none"
          style={{
            lineHeight: 1.7,
            color: '#1f2937',
          }}
        />
      )}
    </>
  );
}
